import client from '@/graphql/client'
import GetAttachmentUploadUrl from '@/graphql/mutations/GetAttachmentUploadUrl'
import GetAttachmentDownloadUrl from '@/graphql/queries/GetAttachmentDownloadUrl'
import GetAvatarUploadUrl from '@/graphql/mutations/GetAvatarUploadUrl'
import DeleteAvatarFile from '@/graphql/mutations/DeleteAvatarFile'

// The browser holds NO AWS credentials. All S3 access is server-side: an
// authorized GraphQL endpoint mints short-lived presigned URLs (or deletes the
// object) after an access check, and the browser does the transfer directly.
// Avatar READS are served by CloudFront (getAvatarCloudFrontUrl).

const AVATAR_PATH_PREFIX = 'profile-pictures/'

// Attachment keys are scoped by projectId as the first segment (`{projectId}/…`)
// or, for AI attachments, the second (`ai-attachments/{projectId}/…`).
const projectIdFromKey = (key) => {
  const parts = (key || '').split('/')

  return parts[0] === 'ai-attachments' ? parts[1] : parts[0]
}

class Storage {
  // ---- Card attachments (org-membership gated) -----------------------------

  // Upload a card attachment. Returns the stored S3 object { bucket, key, region }.
  async uploadAttachment ({ projectId, workflowId, cardId, filename, file }) {
    const { getAttachmentUploadUrl } = await client.query({
      operationName: 'getAttachmentUploadUrl',
      query: GetAttachmentUploadUrl,
      variables: { projectId, workflowId, cardId, filename }
    })
    const { url, key, bucket, region } = getAttachmentUploadUrl
    await fetch(url, { method: 'PUT', body: file })

    return { bucket, key, region }
  }

  // Presigned GET URL for an existing attachment key (projectId derived from the key).
  async getAttachmentUrl (key) {
    const projectId = projectIdFromKey(key)
    const { getAttachmentDownloadUrl } = await client.query({
      operationName: 'getAttachmentDownloadUrl',
      query: GetAttachmentDownloadUrl,
      variables: { projectId, key }
    })

    return getAttachmentDownloadUrl ? getAttachmentDownloadUrl.url : null
  }

  // ---- Avatars (self-scoped; reads via CloudFront) -------------------------

  // Upload the caller's own avatar. Returns the stored S3 object { bucket, key, region }.
  async uploadAvatar ({ filename, file }) {
    const { getAvatarUploadUrl } = await client.query({
      operationName: 'getAvatarUploadUrl',
      query: GetAvatarUploadUrl,
      variables: { filename }
    })
    const { url, key, bucket, region } = getAvatarUploadUrl
    await fetch(url, { method: 'PUT', body: file })

    return { bucket, key, region }
  }

  // Delete an avatar object (server rejects anything outside the caller's prefix).
  async deleteAvatar (key) {
    await client.query({
      operationName: 'deleteAvatarFile',
      query: DeleteAvatarFile,
      variables: { key }
    })
  }

  isAvatarPath (key) {
    return key && key.startsWith(AVATAR_PATH_PREFIX)
  }

  getAvatarCloudFrontUrl (key) {
    const domain = process.env.VUE_APP_CLOUDFRONT_AVATAR_DOMAIN

    if (!domain) {
      console.warn('VUE_APP_CLOUDFRONT_AVATAR_DOMAIN not configured for avatars')
      return null
    }

    if (!this.isAvatarPath(key)) {
      console.warn(
        `Key "${key}" is not an avatar path (${AVATAR_PATH_PREFIX}*). CloudFront URL only available for avatars.`)
      return null
    }

    return `https://${domain}/${key}`
  }

  extractKeyFromPresignedUrl (url) {
    try {
      if (!url) return null

      const urlWithoutQuery = url.split('?')[0]
      const urlObj = new URL(urlWithoutQuery)
      const path = urlObj.pathname

      return path.startsWith('/') ? path.substring(1) : path
    } catch (error) {
      console.error('Error extracting key from URL:', error)
      return null
    }
  }
}

Storage.region = process.env.VUE_APP_AWS_REGION

export default Storage
